import React, { useEffect, PropsWithChildren } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { PermissionType } from 'src/types/permission';
import { useHasPermission } from 'src/use-cases/user/useUserPermissions';

interface RequirePermissionProps extends PropsWithChildren {
  permission: PermissionType;
}

const RequirePermission: React.FC<RequirePermissionProps> = ({ children, permission }) => {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const navigate = useNavigate();
  // Permissions are cached from UserDataSynchronizer - no duplicate API calls
  const { hasPermission, isLoading } = useHasPermission(permission);

  useEffect(() => {
    if (authStatus === 'unauthenticated') {
      navigate('/login');
    }
  }, [authStatus, navigate]);

  // Redirect to forbidden page if user doesn't have permission
  useEffect(() => {
    if (!isLoading && !hasPermission) {
      navigate('/forbidden');
    }
  }, [hasPermission, isLoading, navigate]);

  if (authStatus === 'unauthenticated') {
    return null;
  }

  // Permissions are cached from UserDataSynchronizer, so isLoading should be false immediately
  // If still loading (edge case), return null to avoid flash - children will render on next tick
  if (isLoading) {
    return null;
  }

  if (!hasPermission) {
    return null;
  }
  return <>{children}</>;
};

export default RequirePermission;
